import * as React from 'react';

import {
  DropZone,
  Text,
  Checkbox,
  humanReadableFileSize,
  humanReadableDate,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { File, FileText, Folder, Image, UploadIcon } from 'lucide-react';
import { isFileDropItem } from 'react-aria-components';

import { $uploadFilesToCurrentDirectory } from '../../_state/fileActions.state';
import {
  $files,
  $selectSingleFile,
  $toggleFileSelection,
  $selectedFileIds,
  $currentFolderId,
  $clearSelection,
  $isSelectionMode,
  $selectAllFiles,
  $searchTerm,
} from '../../_state/files.state';
import { en as t } from '../../i18n/_en';
import { FileRecord } from '../../types/files';

import { FileActionMenu } from './FileActionMenu';

import { getLocale } from '~/shared/utils/locale';

export const FileList = () => {
  const toast = useToast();
  const files = useAtomValue($files);
  const selectedFileIds = useAtomValue($selectedFileIds);
  const isSelectionMode = useAtomValue($isSelectionMode);
  const searchTerm = useAtomValue($searchTerm);
  const selectSingleFile = useSetAtom($selectSingleFile);
  const toggleFileSelection = useSetAtom($toggleFileSelection);
  const clearSelection = useSetAtom($clearSelection);
  const selectAllFiles = useSetAtom($selectAllFiles);
  const [currentFolderId, setCurrentFolderId] = useAtom($currentFolderId);
  const uploadFiles = useSetAtom($uploadFilesToCurrentDirectory);
  const [isDragging, setIsDragging] = React.useState(false);
  const isSearching = !!searchTerm?.trim();

  // Clear selection when folder changes
  React.useEffect(() => {
    clearSelection();
  }, [currentFolderId, clearSelection]);

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      selectAllFiles();
    } else {
      clearSelection();
    }
  };

  const handleDoubleClick = (file: FileRecord) => {
    if (file.type === 'Directory') {
      setCurrentFolderId(file.id);
    } else {
      // Open file preview
      window.open(file.url, '_blank');
    }
  };

  const getFileIcon = (file: FileRecord) => {
    if (file.type === 'Directory') {
      return <Folder className="size-5 text-primary opacity-50" />;
    }

    if (file.mimeType.startsWith('image/')) {
      return <Image className="size-5 text-success opacity-50" />;
    }

    if (file.mimeType === 'application/pdf') {
      return <FileText className="size-5 text-failure opacity-50" />;
    }

    return <File className="size-5 text-neutral opacity-50" />;
  };

  const handleDrop = async (
    e: Parameters<
      NonNullable<React.ComponentProps<typeof DropZone>['onDrop']>
    >[0],
  ) => {
    const fileItems = e.items.filter((item) => isFileDropItem(item));
    if (!fileItems.length) return;

    const filePromises = fileItems.map((item) => item.getFile());
    const uploadedFiles = await Promise.all(filePromises);

    // Upload files using the bulk upload function
    const results = await uploadFiles(uploadedFiles);

    // Show success and error messages
    results.successful.forEach(({ file }) => {
      toast.success(`Uploaded ${file.name}`);
    });

    results.failed.forEach(({ file, error }) => {
      toast.error(`Failed to upload ${file.name}: ${error}`);
    });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the component entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (files.length === 0) {
    return (
      <div
        className="relative h-96 w-full"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
      >
        <div className="flex h-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral/20">
          <UploadIcon className="mb-4 size-12 text-neutral/40" />
          <p className="text-center text-neutral/60">
            {isSearching ? (
              <>
                {t.fileExplorer.empty.noSearchResults}
                <br />
                <span className="text-sm">
                  {t.fileExplorer.empty.searchResultsHint}
                </span>
              </>
            ) : (
              <>
                {t.fileExplorer.empty.noFilesInFolder}
                <br />
                <span className="text-sm">
                  {t.fileExplorer.empty.dragDropHint}
                </span>
              </>
            )}
          </p>
        </div>

        {isDragging && (
          <DropZone
            onDrop={handleDrop}
            onDropEnter={() => setIsDragging(true)}
            onDropExit={() => setIsDragging(false)}
            className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-primary/40 bg-primary/5"
          >
            <Text slot="label">Drop files here to upload</Text>
            <div className="flex flex-col items-center">
              <UploadIcon className="mb-2 size-16 text-primary/50" />
              <p className="font-medium text-primary/70">
                Drop files to upload
              </p>
            </div>
          </DropZone>
        )}
      </div>
    );
  }

  return (
    <div
      className="@FileList relative w-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
    >
      <div className="overflow-x-auto">
        <table className="w-full" role="grid" aria-label="File list">
          <thead className="border-b bg-neutral/5">
            <tr role="row">
              {isSelectionMode && (
                <th className="w-12 px-4 py-2 text-left" role="columnheader">
                  <Checkbox
                    isSelected={
                      selectedFileIds.size === files.length && files.length > 0
                    }
                    isIndeterminate={
                      selectedFileIds.size > 0 &&
                      selectedFileIds.size < files.length
                    }
                    onChange={handleSelectAll}
                    aria-label="Select all files"
                  />
                </th>
              )}
              <th
                className="px-4 py-2 text-left text-sm font-medium text-neutral/70"
                role="columnheader"
              >
                Name
              </th>
              <th
                className="px-4 py-2 text-left text-sm font-medium text-neutral/70"
                role="columnheader"
              >
                Size
              </th>
              <th
                className="px-4 py-2 text-left text-sm font-medium text-neutral/70"
                role="columnheader"
              >
                Type
              </th>
              <th
                className="px-4 py-2 text-left text-sm font-medium text-neutral/70"
                role="columnheader"
              >
                Modified
              </th>
              <th
                className="px-4 py-2 text-right text-sm font-medium text-neutral/70"
                role="columnheader"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => (
              <tr
                key={file.id}
                role="row"
                aria-selected={selectedFileIds.has(file.id)}
                className={cn(
                  'cursor-pointer border-b hover:bg-neutral/5',
                  selectedFileIds.has(file.id) ? 'bg-primary/5' : '',
                )}
                onClick={(e) => {
                  if (isSelectionMode) {
                    toggleFileSelection(file.id);
                  } else if (e.ctrlKey || e.metaKey) {
                    toggleFileSelection(file.id);
                  } else {
                    selectSingleFile(file.id);
                  }
                }}
                onDoubleClick={() => handleDoubleClick(file)}
              >
                {isSelectionMode && (
                  <td className="w-12 px-4 py-2" role="gridcell">
                    <Checkbox
                      isSelected={selectedFileIds.has(file.id)}
                      onChange={() => toggleFileSelection(file.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${file.name}`}
                    />
                  </td>
                )}
                <td className="px-4 py-2" role="gridcell">
                  <div className="flex items-center gap-2">
                    {getFileIcon(file)}
                    <span className="text-sm">{file.name}</span>
                  </div>
                </td>
                <td
                  className="px-4 py-2 text-sm text-neutral/60"
                  role="gridcell"
                >
                  {file.type === 'Directory'
                    ? '-'
                    : humanReadableFileSize(file.size)}
                </td>
                <td
                  className="px-4 py-2 text-sm text-neutral/60"
                  role="gridcell"
                >
                  {file.type === 'Directory' ? 'Folder' : file.mimeType}
                </td>
                <td
                  className="px-4 py-2 text-sm text-neutral/60"
                  role="gridcell"
                >
                  {humanReadableDate(file.updatedAt, { locale: getLocale() })}
                </td>
                <td className="px-4 py-2 text-right" role="gridcell">
                  <div onClick={(e) => e.stopPropagation()}>
                    <FileActionMenu file={file} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isDragging && (
        <DropZone
          onDrop={handleDrop}
          onDropEnter={() => setIsDragging(true)}
          onDropExit={() => setIsDragging(false)}
          className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-primary/40 bg-primary/5"
        >
          <Text slot="label">Drop files here to upload</Text>
          <div className="flex flex-col items-center">
            <UploadIcon className="mb-2 size-16 text-primary/50" />
            <p className="font-medium text-primary/70">Drop files to upload</p>
          </div>
        </DropZone>
      )}
    </div>
  );
};
